/**
 * @module root/shared
 * @copyright 1994-2017 T-Mobile US, Inc. All Rights Reserved.
 */
// Core Libraries
import {
  Component,
  Output,
  OnInit,
  OnDestroy,
  EventEmitter,
  ViewChild,
  ElementRef,
  Input,
} from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { Subject } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';
import { takeUntil, debounceTime } from 'rxjs/operators';
// Interfaces
import { GlobalMessagesInterface } from '@core/aem/interfaces/global-messages/global-messages.interface';
// Services
import { SearchService } from '@root/shared/services/search.service';
import { CommonUtilService } from '@support/shared/services/common-util.service';
// Models
import { SearchSuggestionModel } from '@root/shared/models/search-suggestion.model';
import { SHARED_CONSTANTS } from '@root/shared/constants/shared-constants.constants';
import { PDLBaseComponent } from '@experience/reb-pdl/components/pdl.base.component';
import { PdlService } from '@experience/reb-pdl/services/pdl.service';
import { NavbarService } from '@root/shared/services/navbar.service';

/**
 * Search component expanded on click of the search icon
 */
@Component({
  selector: 'tmo-search',
  templateUrl: './search.component.html',
})
export class SearchComponent extends PDLBaseComponent implements OnInit, OnDestroy {
  /**
   * AEM data for the global nav search component
   */
  @Input() public aemContent: GlobalMessagesInterface;
  /**
   * Event emitter to close searchbox when the search is initiated
   */
  @Output() public closeSearchBox: EventEmitter<boolean>;

  /**
   * Event emitter to enable searchBox focus when the search is initiated
   */
  @ViewChild('searchResult', { static: false }) public searchElement: ElementRef;
  /**
   * Form control used to track the search input field
   */
  public searchFieldFormControl: UntypedFormControl;

  /**
   * Boolean set if new search is being initiated
   */
  public searchLoaded: boolean;

  /**
   * Boolean set if input is of length 5 and numeric
   */
  public isInputZipCode: boolean;

  /**
   * Stores value of term searched
   */
  public inputSearchTerm: string;

  /**
   * Model storing what is to be displayed in the search results
   */
  public searchSuggestionModel: SearchSuggestionModel;

  /**
   * Minimum search length to trigger GO CTA and search suggestions
   */
  public searchTermMinLength: number;

  /**
   * Unsubscriber subject
   */
  private ngUnsubscribe: Subject<void>;

  /**
   * Web channel flag
   */
  // private isWebChannel: boolean;
  private isDesktop: boolean;
  public eventData: object;

  constructor(
    private searchService: SearchService,
    private router: Router,
    private commonUtilService: CommonUtilService,
    public el: ElementRef,
    public pdlService: PdlService,
    public route: ActivatedRoute,
    private navBarService: NavbarService
  ) {
    super(pdlService, route);
    this.searchFieldFormControl = new UntypedFormControl();
    const minimumLimitForSearchSuggestions = 2;
    this.searchTermMinLength = minimumLimitForSearchSuggestions;
    this.closeSearchBox = new EventEmitter();
    this.searchSuggestionModel = null;
    this.searchLoaded = false;
    this.isInputZipCode = false;
    this.inputSearchTerm = '';
    this.searchElement = el;
    this.ngUnsubscribe = new Subject<void>();
  }
  /**
   * Enables searchBox cursor focus
   *
   */
  public enableSearchFocus(): void {
    this.searchElement.nativeElement.focus();
  }
  /**
   * Loads the initial configuration of Search
   * @returns void
   */
  public ngOnInit(): void {
    this.enableSearchFocus();
    const searchDebounceTime = 1000;
    // this.isWebChannel = SETTINGS.CHANNEL.toLocaleLowerCase() === 'web';
    this.isDesktop = this.navBarService.isDesktop();
    // debounce keystroke events
    this.searchFieldFormControl.valueChanges
      .pipe(debounceTime(searchDebounceTime), takeUntil(this.ngUnsubscribe))
      .subscribe((newSearch: string) => {
        if (newSearch.length > this.searchTermMinLength) {
          this.inputSearchTerm = newSearch;
          this.initiateSearchSuggestions(newSearch);
        }
      });
  }

  /**
   * Destroys all the subscriptions
   */
  public ngOnDestroy(): void {
    super.ngOnDestroy();
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  /**
   * Navigates to search results page
   */
  public goToSearchResults(): void {
    if (this.inputSearchTerm.length > this.searchTermMinLength) {
      this.searchService.cacheNewSearchTerm(this.inputSearchTerm);
      this.closeSearch();
      const targetUrl =
        window.location.protocol +
        '//' +
        window.location.host +
        SHARED_CONSTANTS.SEARCH_NAVIGATE +
        '/' +
        this.inputSearchTerm;
      this.triggerClickEvent(this.inputSearchTerm, '0', 'Go', targetUrl);
      this.router.navigate([SHARED_CONSTANTS.SEARCH_NAVIGATE, this.inputSearchTerm]).then(() => {
        this.searchService.searchEventTrigger(SHARED_CONSTANTS.FREE_FORM_TEXT);
      });
    }
  }

  /**
   * To redirect user to store locator page.
   * @param url - String url of the new route
   * @param isNewTab - boolean to open in new tab or not
   * @returns void
   */
  public navigateToStoreLocator(url: string, isNewTab: boolean): void {
    this.closeSearch();
    this.commonUtilService.routeToGivenUrl(url, isNewTab);
  }

  /**
   * Hides suggested search results after selecting any of the result.
   */
  public selectSuggestedSearch(selectedResult: string, category?: string): void {
    this.searchService.cacheNewSearchTerm(this.inputSearchTerm);
    this.searchFieldFormControl.setValue(selectedResult);
    const targetUrl =
      window.location.protocol +
      '//' +
      window.location.host +
      SHARED_CONSTANTS.SEARCH_NAVIGATE +
      '/' +
      selectedResult;
    this.triggerClickEvent(selectedResult, '1', category, targetUrl);
    this.closeSearch();
    this.router.navigate([SHARED_CONSTANTS.SEARCH_NAVIGATE, selectedResult]).then(() => {
      this.searchService.searchEventTrigger(selectedResult);
    });
  }

  /**
   * Function retrieves new search term suggestions based on previous searches
   * and suggestions API response
   * @param newSearch New Search term
   */
  private initiateSearchSuggestions(newSearch: string): void {
    this.searchLoaded = false;
    this.inputSearchTerm = newSearch;
    if (this.searchService.isZipCodeSearch(newSearch)) {
      this.searchLoaded = true;
      this.isInputZipCode = true;
    } else {
      this.isInputZipCode = false;
      this.searchService
        .initiateSearchSuggestions(newSearch)
        .pipe(takeUntil(this.ngUnsubscribe))
        .subscribe((newSearchSuggestionModel: SearchSuggestionModel) => {
          this.searchLoaded = true;
          this.searchSuggestionModel = newSearchSuggestionModel;
        });
    }
  }
  /**
   * Method to Close Search Box
   * @returns void
   */
  public closeSearch(): void {
    this.closeSearchBox.emit(true);
  }

  private triggerClickEvent(
    searchTerm?: string,
    autosearch?: string,
    category?: string,
    targetURL?: string
  ): void {
    // if (this.isWebChannel) {
    this.eventData = {
      eventInfo: {
        eventAction: 'click',
        eventType: 'navigation',
      },
      eventDescription: {
        navType: this.isDesktop ? 'tNav' : 'hNav',
        category: category ? category : '',
        subCategory: '',
        targetUrl: targetURL ? targetURL : '',
      },
      searchTerm: searchTerm ? searchTerm : '',
      autosearch: autosearch ? autosearch : '',
    };
    this.pdlService.triggerCustomEvent(this.pdlPageComponentId, 'click');
    // }
  }
}
